import { Download } from "lucide-react";
import { shortDate } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATS = ["pts", "reb", "ast", "fg3m"];
const f1 = (v) => Number(v).toFixed(1);
export const teamLogo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);

// Same formulas as the backend summary, applied to the visible games.
export function summarize(games) {
  const sides = games.flatMap((g) => [g.away, g.home]);
  if (!sides.length) return null;
  const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
  return {
    teams: sides.length, ...Object.fromEntries(STATS.map((s) => [`mae_${s}`, mean(sides.map((t) => Math.abs(t.error[s])))])),
    bias_pts: mean(sides.map((t) => t.error.pts)),
    winner_pct: mean(games.map((g) => (g.winner_correct ? 1 : 0))),
    mae_game_total: mean(games.map((g) => Math.abs(g.proj_total - g.actual_total))),
  };
}

export function DateFilter({ days, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="aa-select" data-testid="tt-date-filter" aria-label="Filter by date"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="tt-date-all">All dates</SelectItem>
        {days.map((d) => <SelectItem key={d} value={d} data-testid={`tt-date-${d}`}>{shortDate(d)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportCsv(games, src, day) {
  const head = ["date", "away", "away_proj", "away_real", "away_error", "home", "home_proj", "home_real", "home_error", "proj_total", "real_total", "winner_correct", "source"];
  const rows = games.map((g) => [g.game_day, g.away.team_code, f1(g.away.proj.pts), g.away.actual.pts, f1(g.away.error.pts),
    g.home.team_code, f1(g.home.proj.pts), g.home.actual.pts, f1(g.home.error.pts), f1(g.proj_total), g.actual_total,
    g.winner_correct ? "yes" : "no", src === "pregame" ? "SNAPSHOT" : "BACKTEST"]);
  const csv = [head, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `accuracy_${src}_${day}.csv` });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

export function ExportButton({ onClick, disabled }) {
  return (
    <button type="button" className="aa-export" onClick={onClick} disabled={disabled} data-testid="tt-export-csv">
      <Download size={15} /> Export CSV
    </button>
  );
}

function Pick({ t, i, kind }) {
  const e = t.error.pts;
  return (
    <li className="aa-pick" data-testid={`tt-${kind}-${i}`}>
      <span className="aa-rank">{i + 1}</span>
      <img src={teamLogo(t.team_code)} alt="" loading="lazy" onError={(ev) => { ev.currentTarget.style.visibility = "hidden"; }} />
      <strong className="aa-code">{t.team_code}</strong>
      <span className="aa-date">{shortDate(t.day)}</span>
      <span className="aa-number">{f1(t.proj.pts)} <span className="aa-vs">vs</span> {t.actual.pts}</span>
      <span className={`aa-delta ${kind === "best" ? "good" : "bad"}`}>{e > 0 ? "+" : ""}{f1(e)}</span>
    </li>
  );
}

export function BestWorst({ games }) {
  const sides = games.flatMap((g) => [{ ...g.away, day: g.game_day, key: `${g.game_id}-a` }, { ...g.home, day: g.game_day, key: `${g.game_id}-h` }])
    .sort((a, b) => Math.abs(a.error.pts) - Math.abs(b.error.pts));
  if (sides.length < 2) return null;
  const n = Math.min(5, Math.floor(sides.length / 2));
  const lists = [["best", "Most accurate", sides.slice(0, n)], ["worst", "Biggest misses", sides.slice(-n).reverse()]];
  return (
    <div className="aa-bestworst" data-testid="tt-best-worst">
      {lists.map(([kind, title, items]) => (
        <div key={kind} className={`aa-bw-panel ${kind}`} data-testid={`tt-${kind}-list`}>
          <h3>{title} <small>team PTS · by absolute error</small></h3>
          <ol>{items.map((t, i) => <Pick key={t.key} t={t} i={i} kind={kind} />)}</ol>
        </div>
      ))}
    </div>
  );
}
